import uuid from 'js-uuid';
import Cookies from 'js-cookie';
import { currentPageToAnalyticsName } from 'javascripts/util/section_name';
import makeDebugger from 'javascripts/util/devLog';

const MAX_TIME_ON_PAGE = 60 * 5; // 5 minutes in seconds
const MAX_EVENT_NAME_LENGTH = 100;
const registeredPages = {};
const timeOnPage = {
  global: null,
  trackers: [],
};
let currentPageViewId = null;
let didSendPageView = {};
let pageChangePath = null;
let events = {};

const debug = makeDebugger('analytics');

// The public api for this module use like:
// AhaAnalytics.track('Feature-123', 'Clicked create feature')
// AhaAnalytics.track('Feature-123', 'Clicked create feature', {
//   section: 'tasks',
// })
// AhaAnalytics.registerPageTracking('Feature-123', /features/)
const AhaAnalytics = {
  pageHasLoaded: false,
  eventQueue: [],
  domain: null,
  identityData: {},

  registerPageTracking(reference, regexOrMatcher, properties = {}) {
    const pageMatcher = { properties };

    if (regexOrMatcher instanceof RegExp) {
      pageMatcher.regex = regexOrMatcher;
    } else {
      pageMatcher.matcher = regexOrMatcher;
    }

    const previouslyRegistered = registeredPages[reference];
    registeredPages[reference] = pageMatcher;
    if (!previouslyRegistered) {
      if (
        AhaAnalytics.pageHasLoaded &&
        !didSendPageView[reference] &&
        pageMatches(pageMatcher)
      ) {
        trackPage(reference, pageMatcher);
      }
    }
  },

  track(reference, eventName, properties = {}) {
    properties['Page name'] = currentPageToAnalyticsName();

    debug('track analytics:', reference, eventName, properties);

    if (!AhaAnalytics.shouldTrack()) {
      return false;
    }
    const pageData = getData();

    const currentTimeOnPage = Math.round(getCurrentTimeOnPage());
    if (currentTimeOnPage > 0) {
      properties.time_on_page = currentTimeOnPage;
    }
    properties.first_occurrence = !events[eventName];
    events[eventName] = true;

    if (eventName.length > MAX_EVENT_NAME_LENGTH) {
      throw new Error(`Event name is too long: ${eventName}`);
    }

    const beaconData = {
      event_name: eventName,
      reference: reference,
      properties: properties,
      path: getPath().path,
    };

    if (eventName.length > 100) {
      throw new Error(`Event name is too long: ${eventName}`);
    }

    // Slightly delay event sending to allow actual requests to go first
    setTimeout(() => {
      if (AhaAnalytics.pageHasLoaded) {
        sendBeacon(addCommonFields(beaconData, pageData));
      } else {
        queueBeacon(addCommonFields(beaconData, pageData));
      }
    }, 50);
  },

  identify(data) {
    this.identityData = data;
  },

  // A user overridable method all specific aha logic should go here
  shouldTrack() {
    if (window.isPublishing) {
      return false;
    }
    if (window.dontTrack) {
      return false;
    }

    const domain = analyticsDomain();
    if (!domain || domain === '') {
      return false;
    }

    const token = analyticsToken();
    if (!token || token === '') {
      return false;
    }

    return true;
  },
};

window.AhaAnalytics = AhaAnalytics;

export default AhaAnalytics;

function setupTracking() {
  trackPageChanges();

  if (AhaAnalytics.shouldTrack()) {
    AhaAnalytics.identify({
      token: analyticsToken(),
    });
    AhaAnalytics.domain = analyticsDomain();
  }

  AhaAnalytics.pageHasLoaded = true;
  drainBeaconQueue();
}

function pageMatches(pageData) {
  const path = getPath();
  return (
    (pageData.regex && path.path.match(pageData.regex)) ||
    (pageData.matcher && pageData.matcher(path) === true)
  );
}

function trackPage(reference, pageData) {
  let { properties } = pageData;
  if (properties instanceof Function) properties = properties();
  trackPageView(reference, properties);
  trackTimeOnPage(reference, properties);
}

function trackPageData(path) {
  let didMatch = false;
  Object.keys(registeredPages).forEach(reference => {
    const pageData = registeredPages[reference];
    if (pageMatches(pageData)) {
      trackPage(reference, pageData);
      didMatch = true;
    }
  });
  if (!didMatch) {
    removeTimeOnPageTracking();
  }
}

function analyticsToken() {
  const meta = document.querySelector('meta[name=analytics-token]');
  if (meta) {
    return meta.content;
  }
  return null;
}

function analyticsDomain() {
  const meta = document.querySelector('meta[name=analytics-domain]');
  if (meta) {
    return meta.content;
  }
  return null;
}

function addCommonFields(data, pageData) {
  data.page_view_id = currentPageViewId || pageData.previousPageviewId || '';
  data.session_id = pageData.sessionId;
  data.id = uuid.v1();
  data.timestamp = new Date();
  data.token = analyticsToken();

  return data;
}

function addDomain(path) {
  return `${analyticsDomain()}${path}`;
}

// This is a relatively new api that is specifically for sending
// analytics on page unload, the browser will ensure this request happens
// even if the page is removed.
function sendBeacon(data) {
  // Due to a chrome bug can't send json content type for sendbeacon.
  const dataBlob = new Blob([JSON.stringify({ data })], { type: 'text/plain' });
  const url = addDomain(`/engagement/track?beacon=true`);
  window.navigator.sendBeacon(url, dataBlob);
}

function queueBeacon(data) {
  AhaAnalytics.eventQueue.push(data);
}

function drainBeaconQueue() {
  let data;
  while ((data = AhaAnalytics.eventQueue.shift())) {
    sendBeacon(data);
  }
}

// sendbeacon is not yet supported in ie so polyfill it with a basic request
// This is has 50/50 chance to not complete
function sendBeaconPolyfill(url, data) {
  const xhr = new XMLHttpRequest();
  xhr.open('POST', url, false);
  xhr.withCredentials = true;
  xhr.setRequestHeader('Accept', '*/*');
  if (typeof data === 'string') {
    xhr.setRequestHeader('Content-Type', 'text/plain;charset=UTF-8');
    xhr.responseType = 'text/plain';
  } else if (Object.prototype.toString.call(data) === '[object Blob]') {
    if (data.type) {
      xhr.setRequestHeader('Content-Type', data.type);
    }
  }

  xhr.send(data);
  return true;
}

if ('navigator' in window && !('sendBeacon' in window.navigator)) {
  window.navigator.sendBeacon = sendBeaconPolyfill;
}

// convert object to query params
function objectToQueryParams(json) {
  const keys = Object.keys(json);

  return `?${keys
    .map(k => {
      return `${encodeURIComponent(k)}=${encodeURIComponent(json[k])}`;
    })
    .join('&')}`;
}

function newVisitorData() {
  return {
    sessionId: uuid.v1(),
    isNewSession: true,
    pagesViewed: [],
    previousPageviewId: '',
    lastSeen: +new Date(),
  };
}

function getData() {
  const thirtyMinsAgo = new Date();
  thirtyMinsAgo.setMinutes(thirtyMinsAgo.getMinutes() - 30);

  let data = Cookies.get('_aha_analytics');
  if (!data) {
    return newVisitorData();
  }

  try {
    data = JSON.parse(data);
  } catch (e) {
    return newVisitorData();
  }

  if (data.lastSeen < +thirtyMinsAgo) {
    data.isNewSession = true;
    data.sessionId = uuid.v1();
  }

  return data;
}

function getPath() {
  let req = window.location;

  // parse canonical, if page has one
  const canonical = document.querySelector('link[rel="canonical"][href]');
  if (canonical) {
    const a = document.createElement('a');
    a.href = canonical.href;

    // use parsed canonical as location object
    req = a;
  }

  // get path and pathname from location or canonical
  let path = req.pathname + req.search;
  const hostname = `${req.protocol}//${req.hostname}`;

  // if parsing path failed, default to main page
  if (!path) {
    path = '/';
  }

  const pathWithoutQueryString = req.pathname || '/';

  return { path, hostname, pathWithoutQueryString };
}

function trackerUrl() {
  return addDomain('/engagement/page_view');
}

function trackPageView(reference, properties) {
  if (didSendPageView[reference]) {
    return;
  }
  didSendPageView[reference] = true;

  debug('track page view:', reference, properties);

  if (!AhaAnalytics.shouldTrack()) {
    return false;
  }

  // ignore prerendered pages
  if (
    'visibilityState' in document &&
    document.visibilityState === 'prerender'
  ) {
    return;
  }

  // only set referrer if not internal
  let referrer = '';
  if (document.referrer.indexOf(location.hostname) < 0) {
    referrer = document.referrer;
  }

  const { path, hostname } = getPath();
  const data = getData();
  const pageViewCollection = addCommonFields(
    {
      path: path,
      host: hostname,
      referrer: referrer,
      reference: reference,
      properties: JSON.stringify(properties),
    },
    data
  );

  const i = document.createElement('img');
  i.src = trackerUrl() + objectToQueryParams(pageViewCollection);
  i.addEventListener('load', () => {
    const today = new Date();
    const expires = new Date();
    expires.setTime(today.getTime() + 3600000 * 24 * 14);

    data.previousPageviewId = currentPageViewId = pageViewCollection.id;
    data.isNewSession = false;
    data.lastSeen = +new Date();
    Cookies.set('_aha_analytics', data, { expires: expires, secure: true });
  });
  document.body.appendChild(i);
  requestAnimationFrame(() => {
    if (i && i.parentElement === document.body) {
      document.body.removeChild(i);
    }
  });
}

function trackPageChanges() {
  // Feature detects to prevent errors in unsupporting browsers.
  if (!history.pushState || !window.addEventListener) return;

  pageChangePath = getPath().path;

  // Watches for history changes.
  const originalPushState = history.pushState;
  history.pushState = function (...args) {
    originalPushState.apply(history, args);
    handleUrlChange(true);
  };

  const originalReplaceState = history.replaceState;
  history.replaceState = function (...args) {
    originalReplaceState.apply(history, args);
    handleUrlChange(false);
  };

  window.addEventListener('popstate', () => {
    handleUrlChange(true);
  });
}

function handleUrlChange(historyDidUpdate) {
  const oldPath = pageChangePath;
  const newPath = getPath().path;

  if (newPath && oldPath && oldPath !== newPath) {
    pageChangePath = newPath;

    if (historyDidUpdate) {
      didSendPageView = {};
      events = {};

      // Calls the update logic asychronously to help ensure that app logic
      // responding to the URL change happens prior to this.
      setTimeout(() => {
        sendTimeOnPage();
        trackPageData(newPath);
      }, 0);
    }
  }
}

function handleVisibilityChange(unload = false) {
  if (timeOnPage.trackers.length === 0 || !timeOnPage.global) {
    return false;
  }

  const timeNow = new Date();
  const newState = document.visibilityState;
  const oldState = timeOnPage.global.visibility;
  const startTimer = oldState === 'hidden' && newState === 'visible';
  let stopTimer =
    (oldState === 'visible' && newState === 'hidden') || // page became hidden (or unloaded in Firefox)
    (oldState === 'visible' && newState === 'unloaded') || // standard unload
    (oldState === 'visible' && unload) || // compatible unload
    (unload && newState === undefined); // unload in legacy browser
  stopTimer = stopTimer && timeOnPage.global.timestamp !== undefined; // timer not running

  if (startTimer) {
    timeOnPage.global.visibility = newState;
    timeOnPage.global.timestamp = timeNow;
  }
  if (stopTimer) {
    timeOnPage.global.visibility = newState;
    timeOnPage.global.timer += Math.min(
      (timeNow - timeOnPage.global.timestamp) / 1000,
      MAX_TIME_ON_PAGE
    );
    delete timeOnPage.global.timestamp;
  }
}

function getCurrentTimeOnPage() {
  if (timeOnPage.trackers.length === 0 || !timeOnPage.global) {
    return 0;
  }

  const timeNow = new Date();
  return (
    timeOnPage.global.timer +
    Math.min((timeNow - timeOnPage.global.timestamp) / 1000, MAX_TIME_ON_PAGE)
  );
}

function sendTimeOnPage() {
  if (!AhaAnalytics.shouldTrack()) {
    return false;
  }

  if (timeOnPage.trackers.length === 0 || !timeOnPage.global) {
    return false;
  }
  if (document.visibilityState && document.visibilityState === 'prerender') {
    return false;
  }

  handleVisibilityChange(true);

  const timeSpent = Math.round(timeOnPage.global.timer);
  if (timeSpent === 0) {
    return false;
  }

  const pageData = getData();
  const beaconData = {
    event_name: 'Time on page',
    path: timeOnPage.global.path,
    time_spent: timeSpent,
  };

  timeOnPage.trackers.forEach(function (tracker) {
    const trackerData = Object.assign({}, beaconData, {
      reference: tracker.reference,
      properties: tracker.properties,
    });

    sendBeacon(addCommonFields(trackerData, pageData));
  });

  removeTimeOnPageTracking();
}

function setupTimeOnPageTracking(reference, properties) {
  if (!timeOnPage.global) {
    timeOnPage.global = {
      path: getPath().path,
      timer: 0,
      timestamp: new Date(),
      visibility: document.visibilityState,
    };
  }
  timeOnPage.trackers.push({
    reference: reference,
    properties: properties,
  });
}

function trackTimeOnPage(reference, properties) {
  if (!AhaAnalytics.shouldTrack()) {
    return false;
  }

  setupTimeOnPageTracking(reference, properties);

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('beforeunload', sendTimeOnPage);
  window.addEventListener('unload', sendTimeOnPage);
}

function removeTimeOnPageTracking() {
  timeOnPage.global = null;
  timeOnPage.trackers = [];
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('beforeunload', sendTimeOnPage);
  window.removeEventListener('unload', sendTimeOnPage);
}

document.addEventListener(
  'DOMContentLoaded',
  () => {
    setupTracking();

    if (Object.keys(registeredPages).length > 0) {
      trackPageData(getPath().path);
    }
  },
  false
);
